import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LockClosedIcon, SparklesIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { UPGRADE_FEATURE_BULLETS } from "../constants/lockedFeatures";

export default function UpgradeModal({
  open,
  title,
  description,
  onClose,
  onUpgrade,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[10050] flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upgrade-modal-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.button
            type="button"
            className="absolute inset-0 bg-[#0a0f1c]/75 backdrop-blur-md"
            aria-label="Close dialog"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="glass relative z-10 w-full max-w-md overflow-hidden border border-white/20 shadow-2xl shadow-black/50"
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ type: "spring", stiffness: 380, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-primaryBlue/30 blur-3xl" />
            <motion.div
              className="pointer-events-none absolute -left-10 bottom-0 h-32 w-32 rounded-full bg-cyan-400/10 blur-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            />

            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 z-20 rounded-full p-1.5 text-white/60 transition hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            <motion.div className="relative px-6 pb-6 pt-8 sm:px-8 sm:pb-8 sm:pt-10">
              <div className="mb-5 flex justify-center">
                <div className="relative">
                  <motion.div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/25 to-orange-600/10 shadow-lg shadow-amber-900/30">
                    <LockClosedIcon className="h-8 w-8 text-amber-300" />
                  </motion.div>
                  <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-primaryBlue shadow-md">
                    <SparklesIcon className="h-4 w-4 text-white" />
                  </span>
                </div>
              </div>

              <p className="mb-1 text-center text-xs font-semibold uppercase tracking-[0.2em] text-amber-300/90">
                Premium feature
              </p>
              <h2
                id="upgrade-modal-title"
                className="text-center text-2xl font-bold tracking-tight text-white sm:text-[1.65rem]"
              >
                {title}
              </h2>
              <p className="mt-3 text-center text-sm leading-relaxed text-gray-300 sm:text-[15px]">
                {description}
              </p>

              <ul className="mt-6 space-y-2.5 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-4">
                {UPGRADE_FEATURE_BULLETS.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-sm text-gray-200"
                  >
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-gray-200 transition hover:bg-white/10 sm:w-auto sm:min-w-[140px]"
                >
                  Maybe Later
                </button>
                <button
                  type="button"
                  onClick={onUpgrade}
                  className="w-full rounded-xl bg-gradient-to-r from-primaryBlue to-[#3d5fc4] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-primaryBlue/30 transition hover:brightness-110 sm:w-auto sm:min-w-[160px]"
                >
                  Upgrade Now
                </button>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
